
import React from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { DateRange } from 'react-day-picker';
import ExportConfigurationForm from './ExportConfigurationForm';
import ExportHistoryList from './ExportHistoryList';
import { ExportRequest } from './types/export';

const DataExportManager: React.FC = () => {
  const { data: exportRequests, refetch } = useQuery({
    queryKey: ['export-requests'],
    queryFn: async () => {
      // Simulate export requests - in real implementation, fetch from backend
      return [
        {
          id: '1',
          exportType: 'users',
          status: 'completed' as const,
          progress: 100,
          createdAt: new Date().toISOString(),
          downloadUrl: '#'
        },
        {
          id: '2',
          exportType: 'content',
          status: 'processing' as const,
          progress: 65,
          createdAt: new Date().toISOString()
        }
      ] as ExportRequest[];
    }
  });

  const createExport = useMutation({
    mutationFn: async (params: {
      exportType: string;
      dateRange: DateRange | undefined;
      selectedTables: string[];
      format: string;
    }) => {
      // Simulate export creation
      await new Promise(resolve => setTimeout(resolve, 1000));
      return { id: Date.now().toString() };
    },
    onSuccess: () => {
      toast({ title: 'Export started', description: 'Your export request has been queued for processing.' });
      refetch();
    }
  });

  const handleExport = (config: {
    exportType: string;
    dateRange: DateRange | undefined;
    selectedTables: string[];
    format: string;
  }) => {
    if (!config.exportType) {
      toast({ title: 'Error', description: 'Please select an export type', variant: 'destructive' });
      return;
    }

    createExport.mutate(config);
  };

  return (
    <div className="space-y-6">
      <ExportConfigurationForm 
        onExport={handleExport}
        isLoading={createExport.isPending}
      />
      <ExportHistoryList exportRequests={exportRequests || []} />
    </div>
  );
};

export default DataExportManager;
